import { FC, useState } from 'react'
import { useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  AssetSize,
  Button,
  ButtonFunction,
  ButtonType,
  Chip,
  ChipSize,
  ChipVariant,
  ContentList,
  ContentListItem,
  Heading,
  HeadingLevel,
  HeadingTag,
  Icon,
  IconName,
  Link,
  LinkFunction,
  LinkSize,
  LinkTarget,
  LinkVariant,
  Table,
  TableStickiness,
  Text,
  TextColor,
  TextTag,
  TextVariant
} from '@enbw/ui-react'
import cn from 'classnames'
import styles from './styles.module.scss'
import { TARIFF_MANAGEMENT_URI } from '@/constants'
import { CardLayout } from '@/components/CardLayout'
import { SalesPartnerName } from '@/domain/contract/types/contract'
import { ContractPageOutletContext } from '@/domain/contract/layouts/ContractDetailsLayout/ContractDetailsLayout'
import { formatDate, FormattedDateMode } from '@/util/date-util'
import { usePageTitle } from '@/application/routing/hooks/usePageTitle'
import { valueOrEmpty } from '@/util/string-util'
import { SalesPartnerLogo } from '@/domain/contract/pages/ContractTariffBookings/components'

export const ContractTariffBookingsPage: FC = () => {
  const { contract } = useOutletContext<ContractPageOutletContext>()
  const { t } = useTranslation()
  const [showAllPastTariffBookings, setShowAllPastTariffBookings] = useState(false)

  usePageTitle(t('contract.tariffBookings.pageTitle', { lastName: contract.masterData.contractPartner.lastName }))

  const { pastTariffBookings, currentTariffBooking, futureTariffBooking } = contract.tariffBookings
  const hasPastSalesPartnerTariff = pastTariffBookings.some((tariffBooking) => !!tariffBooking.salesPartnerName)
  const visiblePastTariffBookings = showAllPastTariffBookings ? pastTariffBookings : pastTariffBookings.slice(0, 2)
  const hasMorePastTariffBookings = pastTariffBookings.length > 2

  const getSalesPartnerDisplayName = (salesPartnerName: SalesPartnerName | undefined): string => {
    if (!salesPartnerName) {
      return t('common.empty')
    }

    switch (salesPartnerName) {
      case SalesPartnerName.ACE:
        return t('contract.tariffBookings.values.salesPartner.ACE')
      case SalesPartnerName.ACV:
        return t('contract.tariffBookings.values.salesPartner.ACV')
      case SalesPartnerName.ADAC:
        return t('contract.tariffBookings.values.salesPartner.ADAC')
      case SalesPartnerName.ALLIANZ:
        return t('contract.tariffBookings.values.salesPartner.ALLIANZ')
      case SalesPartnerName.ENBW_COMMODITY:
        return t('contract.tariffBookings.values.salesPartner.ENBW_COMMODITY')
      case SalesPartnerName.ENMA:
        return t('contract.tariffBookings.values.salesPartner.ENMA')
      case SalesPartnerName.HYUNDAI:
        return t('contract.tariffBookings.values.salesPartner.HYUNDAI')
      case SalesPartnerName.LBBW:
        return t('contract.tariffBookings.values.salesPartner.LBBW')
      case SalesPartnerName.MOVE:
        return t('contract.tariffBookings.values.salesPartner.MOVE')
      case SalesPartnerName.YELLO:
        return t('contract.tariffBookings.values.salesPartner.YELLO')
    }
  }

  const getTariffManagementUrl = (tariffId: number): string => {
    return `${TARIFF_MANAGEMENT_URI}/displayTariffVersion/${tariffId}`
  }

  const renderTariffManagementLink = (tariffId: number, linkSize: LinkSize = LinkSize.Default, iconSize: AssetSize = AssetSize.XXSmall) => (
    <Link
      href={getTariffManagementUrl(tariffId)}
      function={LinkFunction.Secondary}
      size={linkSize}
      variant={LinkVariant.Text}
      target={LinkTarget.Blank}
      label={String(tariffId)}
    >
      <Icon slot="trailing-icon" name={IconName.OpenInNew} size={iconSize} />
    </Link>
  )

  return (
    <section className={styles.contractTariffBookingsPage}>
      <Heading level={HeadingLevel.H2} tag={HeadingTag.H2}>
        {t('contract.tariffBookings.title')}
      </Heading>

      <div className={styles.cardsGrid}>
        <CardLayout
          title={
            <div className={styles.cardTitleRow}>
              <Heading level={HeadingLevel.H3} tag={HeadingTag.H3}>
                {t('contract.tariffBookings.currentTariffBooking.title')}
              </Heading>
              <Chip label={t('contract.tariffBookings.values.status.active')} size={ChipSize.Small} variant={ChipVariant.BrandSecondary4} />
            </div>
          }
          padded
        >
          {currentTariffBooking ? (
            <div className={styles.tariffBookingContent}>
              <Heading level={HeadingLevel.H4} tag={HeadingTag.H4}>
                <span className={styles.cardTitleRow}>
                  <SalesPartnerLogo name={currentTariffBooking.salesPartnerName} />
                  <span>{currentTariffBooking.tariffName}</span>
                </span>
              </Heading>

              <ContentList>
                <div className={styles.itemsGrid}>
                  <ContentListItem
                    label={t('contract.tariffBookings.fields.validFrom')}
                    body={formatDate(currentTariffBooking.validFrom, FormattedDateMode.DateAndTime)}
                  />
                  <ContentListItem
                    label={t('contract.tariffBookings.fields.validUntil')}
                    body={formatDate(currentTariffBooking.validUntil, FormattedDateMode.DateAndTime)}
                  />
                  <ContentListItem
                    label={t('contract.tariffBookings.fields.bookedAt')}
                    body={formatDate(currentTariffBooking.bookedAt, FormattedDateMode.DateAndTime)}
                    className={styles.fullRowItem}
                  />
                  {currentTariffBooking.salesPartnerName && (
                    <>
                      <ContentListItem
                        label={t('contract.tariffBookings.fields.salesPartner')}
                        body={getSalesPartnerDisplayName(currentTariffBooking.salesPartnerName)}
                      />
                      <ContentListItem
                        label={t('contract.tariffBookings.fields.salesPartnerCustomerReference')}
                        body={valueOrEmpty(currentTariffBooking.salesPartnerCustomerReferenceId)}
                      />
                    </>
                  )}
                  <ContentListItem label={t('contract.tariffBookings.fields.tariffId')}>
                    <span slot="body">{renderTariffManagementLink(currentTariffBooking.tariffId)}</span>
                  </ContentListItem>
                </div>
              </ContentList>
            </div>
          ) : (
            <Text variant={TextVariant.BodyStandard} tag={TextTag.P} color={TextColor.Muted}>
              {t('contract.tariffBookings.currentTariffBooking.empty')}
            </Text>
          )}
        </CardLayout>

        {futureTariffBooking && (
          <CardLayout
            title={
              <div slot="title" className={cn(styles.cardTitleRow, styles.dimmedChildren)}>
                <Heading level={HeadingLevel.H3} tag={HeadingTag.H3}>
                  {t('contract.tariffBookings.futureTariffBooking.title')}
                </Heading>
                <Chip
                  className={styles.notDimmed}
                  label={t('contract.tariffBookings.values.status.pending')}
                  size={ChipSize.Small}
                  variant={ChipVariant.BrandSecondary3}
                />
              </div>
            }
            padded
            dimmed
          >
            <div className={styles.tariffBookingContent}>
              <Heading level={HeadingLevel.H4} tag={HeadingTag.H4}>
                <span className={cn(styles.cardTitleRow, styles.dimmedChildren)}>
                  <SalesPartnerLogo name={futureTariffBooking.salesPartnerName} />
                  <span>{futureTariffBooking.tariffName}</span>
                </span>
              </Heading>

              <ContentList>
                <div className={cn(styles.itemsGrid, styles.dimmedChildren)}>
                  <ContentListItem
                    label={t('contract.tariffBookings.fields.validFrom')}
                    body={formatDate(futureTariffBooking.validFrom, FormattedDateMode.DateAndTime)}
                  />
                  <ContentListItem
                    label={t('contract.tariffBookings.fields.validUntil')}
                    body={formatDate(futureTariffBooking.validUntil, FormattedDateMode.DateAndTime)}
                  />
                  <ContentListItem
                    label={t('contract.tariffBookings.fields.bookedAt')}
                    body={formatDate(futureTariffBooking.bookedAt, FormattedDateMode.DateAndTime)}
                    className={styles.fullRowItem}
                  />
                  {futureTariffBooking.salesPartnerName && (
                    <>
                      <ContentListItem
                        label={t('contract.tariffBookings.fields.salesPartner')}
                        body={getSalesPartnerDisplayName(futureTariffBooking.salesPartnerName)}
                      />
                      <ContentListItem
                        label={t('contract.tariffBookings.fields.salesPartnerCustomerReference')}
                        body={valueOrEmpty(futureTariffBooking.salesPartnerCustomerReferenceId)}
                      />
                    </>
                  )}
                  <ContentListItem className={styles.notDimmed} label={t('contract.tariffBookings.fields.tariffId')}>
                    <span slot="body">{renderTariffManagementLink(futureTariffBooking.tariffId)}</span>
                  </ContentListItem>
                </div>
              </ContentList>
            </div>
          </CardLayout>
        )}
      </div>

      <CardLayout
        title={
          <div className={styles.cardTitleRow}>
            <Heading level={HeadingLevel.H3} tag={HeadingTag.H3}>
              {t('contract.tariffBookings.pastTariffBookings.title')}
            </Heading>
            {/* TODO #4497673 mtirpitz: wait for UI library to support "ChipVariant.Neutral" */}
            <Chip label={t('contract.tariffBookings.values.status.inactive')} size={ChipSize.Small} variant={ChipVariant.Deactivated} />
          </div>
        }
        padded
      >
        {pastTariffBookings.length > 0 ? (
          <div className={styles.pastTariffsTableContainer}>
            <Table sticky={TableStickiness.None}>
              <table>
                <thead>
                  <tr>
                    {/* TODO #4884057 mtirpitz: wait for UI library to support narrow table columns and shrink the first column containing the logos */}
                    <th></th>
                    {hasPastSalesPartnerTariff && (
                      <>
                        <th>{t('contract.tariffBookings.fields.salesPartner')}</th>
                        <th>{t('contract.tariffBookings.fields.salesPartnerCustomerReference')}</th>
                      </>
                    )}
                    <th>{t('contract.tariffBookings.fields.tariffName')}</th>
                    <th>{t('contract.tariffBookings.fields.validFrom')}</th>
                    <th>{t('contract.tariffBookings.fields.validUntil')}</th>
                    <th>{t('contract.tariffBookings.fields.bookedAt')}</th>
                    <th>{t('contract.tariffBookings.fields.tariffId')}</th>
                  </tr>
                </thead>

                <tbody>
                  {visiblePastTariffBookings.map((tariffBooking) => {
                    return (
                      <tr key={`${tariffBooking.tariffId}-${tariffBooking.bookedAt.toISOString()}`}>
                        <td>
                          <SalesPartnerLogo name={tariffBooking.salesPartnerName} size="small" />
                        </td>
                        {hasPastSalesPartnerTariff && (
                          <>
                            <td>{getSalesPartnerDisplayName(tariffBooking.salesPartnerName)}</td>
                            <td>{valueOrEmpty(tariffBooking.salesPartnerCustomerReferenceId)}</td>
                          </>
                        )}
                        <td>{tariffBooking.tariffName}</td>
                        <td>{formatDate(tariffBooking.validFrom, FormattedDateMode.DateAndTime)}</td>
                        <td>{formatDate(tariffBooking.validUntil, FormattedDateMode.DateAndTime)}</td>
                        <td>{formatDate(tariffBooking.bookedAt, FormattedDateMode.DateAndTime)}</td>
                        <td>{renderTariffManagementLink(tariffBooking.tariffId, LinkSize.Small, AssetSize.XXSmall)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </Table>

            {hasMorePastTariffBookings && (
              <div className={styles.pastTariffsTableActions}>
                <Button
                  type={ButtonType.Button}
                  function={ButtonFunction.Tertiary}
                  label={
                    showAllPastTariffBookings
                      ? t('contract.tariffBookings.pastTariffBookings.showLess')
                      : t('contract.tariffBookings.pastTariffBookings.showMore')
                  }
                  onClick={() => setShowAllPastTariffBookings((currentValue) => !currentValue)}
                >
                  <Icon slot="trailing-icon" name={showAllPastTariffBookings ? IconName.ChevronUp : IconName.ChevronDown} size={AssetSize.XSmall} />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Text variant={TextVariant.BodyStandard} tag={TextTag.P} color={TextColor.Muted}>
            {t('contract.tariffBookings.pastTariffBookings.empty')}
          </Text>
        )}
      </CardLayout>
    </section>
  )
}
